import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, SimulationEdit, SimulationShare, SimulationDownload } from "~misc/icons";
import { initialPath } from "~misc/paths";
import { Link } from "react-router-dom";
import ModalSimulacaoPDF from "../modals/ModalSimulacaoPDF";
import ModalResponse from "~components/Modal/ModalResponse";
import ModalAction from "~components/Modal/ModalAction";
import { WarningIcon } from "~misc/icons";
import ModalEmail from "~components/Modal/ModalEmail";
import useUsuario from "~fetchs/useUsuario";
import { useCompartilharSimulacao } from "~fetchs/useSimulacoes";
import LoaderFull from "~components/Form/components/LoaderFull";
import { PDFContent } from "../modals/PDFContent";
import { pdf, BlobProvider } from "@react-pdf/renderer";
import useSimulacao from "~fetchs/useSimulacao";
import { money } from "~hooks/useHelpers";
import useSimulacaoHook from "~pages/Simulators/hooks/useSimulacaoHook";
import usePrazoRegraSimuladores from "~fetchs/usePrazoRegraSimuladores";

const ListagemItem = ({ simulacao, desconto, cliente }) => {
  const [modalPdf, setModalPdf] = useState(false);
  const { usuario } = useUsuario();
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [modalResponse, setModalResponse] = useState({ isOpen: false, message: "", status: "success" });
  const [email, setEmail] = useState(cliente?.email || usuario?.email);
  const [enable, setEnable] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const compartilhar = useCompartilharSimulacao(enable, simulacao.simulacaoId, email, attachment);

  const {
    simulacaoAtualizada,
    grupos,
    todosOsProdutos,
    getTodayDate,
    calcCif,
    calcCifModal,
    atualizarGruposProduto,
    simulation,
    values,
  } = useSimulacaoHook(simulacao, desconto, cliente);

  const familiasIds = (simulacao?.gruposProduto || [])
  const { prazoRegras } = usePrazoRegraSimuladores({
    gruposegmento: cliente?.grupoSegmentoId,
    familias: familiasIds
  });

  const searchDias = (familiaId, prazoRegras) => {
    // Procurar no array de prazoRegras pelo id da família
    const prazoRegra = prazoRegras.find((regra) =>
      regra.familias.some((familia) => familia.id === familiaId)
    );
    // Se encontrar, retorna o valor de dias
    return prazoRegra ? `${prazoRegra.dias } dias`: '-'; // Caso não encontre, retorna null
  };

  const generatePDFFile = async () => {
    const simulacaoAtualizada = atualizarGruposProduto(simulation, simulacao);
    const todosOsProdutos = grupos.reduce((acc, grupo) => acc.concat(grupo.produtos), []);

    const values = {
      fracionario: simulacao.fracionamento,
      descarga: simulacao.descargaManual,
    };

    try {
      const blob = await pdf(
        <PDFContent
          grupos={grupos}
          values={values}
          cliente={cliente}
          usuario={usuario}
          simulacao={simulacao}
          simulacaoAtualizada={simulacaoAtualizada}
          todosOsProdutos={todosOsProdutos}
          calcCif={(preco, qtd, produto) => calcCif(preco, qtd, produto, values)}
          calcCifModal={(precoCif, item) => calcCifModal(precoCif, item, values)}
          getTodayDate={getTodayDate}
          prazoRegras={prazoRegras}
          searchDias={searchDias}
        />
      ).toBlob();

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.log("Erro ao gerar PDF:", error);
      return null;
    }
  };

  useEffect(() => {
    async function initFileAttach() {
      const file = await generatePDFFile();
      setAttachment(file);
    }

    initFileAttach();
  }, [simulacao]);

  const handleSend = async (values) => {
    setEmail(values?.email);
    setShow(false);
    setLoading(true);

    if (!enable) setEnable(true);
    else compartilhar.refetch();
  };

  useEffect(() => {
    if (
      compartilhar?.data != undefined &&
      !compartilhar?.isFetching &&
      compartilhar?.isFetched &&
      compartilhar.isSuccess &&
      enable
    ) {
      setLoading(false);
      setModalResponse({ isOpen: true, message: "E-mail enviado com sucesso!", status: "success" });
    } else if (
      compartilhar?.data != undefined &&
      !compartilhar?.isFetching &&
      compartilhar?.isFetched &&
      compartilhar.isError &&
      enable
    ) {
      setLoading(false);
      setModalResponse({ isOpen: true, message: "Ocorreu um erro ao tentar enviar o e-mail!", status: "error" });
    }
  }, [compartilhar?.data, compartilhar?.isFetching]);

  return (
    <>
      <article className="bg-white px-10 py-12 mt-6 items-center rounded-lg shadow-lg hover:border-terciaria border border-solid transition-all relative">
        {/* Card Header */}
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-2xl font-bold">
            Simulação {simulacao.simulacaoId} - {simulacao.simulador}
          </p>
          <p className="flex items-center text-xl">
            <CalendarIcon />
            <span className="ml-4">Data: </span>
            <time dateTime={simulacao.createdAt} className="font-bold">
              {format(new Date(simulacao.createdAt), "dd/MM/yyyy")} - {format(new Date(simulacao.createdAt), "HH:mm")}
            </time>
          </p>
        </div>
        {/* Card divider */}
        <span className="inline-block my-4 h-px w-full bg-gray-200"></span>
        {/* Card Body */}
        <div className="flex gap-8 md:gap-0 flex-col md:flex-row justify-between">
          <ul className="text-gray-600 text-center md:text-left">
            {simulacao.gruposMateriais.map((grupoMaterial, index) => (
              <li key={grupoMaterial.grupoMaterialId}>
                <strong>{(grupoMaterial.produtos || []).length}</strong> {grupoMaterial.grupoMaterial}
              </li>
            ))}
          </ul>
          <ul className="flex gap-3 justify-center md:justify-normal">
            <li>
              <Link
                to={`${initialPath}/simuladores/${simulacao.clienteId}/simulacao/${simulacao.simulacaoId}`}
                title="Editar"
              >
                <SimulationEdit />
              </Link>
            </li>
            <li>
              <div
                className="cursor-pointer"
                title="Compartilhar"
                onClick={() => {
                  setShow(true);
                }}
              >
                <SimulationShare />
              </div>
            </li>
            <li>
              <div className="cursor-pointer" title="Download" onClick={() => setModalPdf(true)}>
                <SimulationDownload />
              </div>
            </li>
          </ul>
        </div>
      </article>
      <ModalSimulacaoPDF
        isOpen={modalPdf}
        onClose={() => {
          setModalPdf(false);
        }}
        // simulacaoItem={simulacao}
        // simuId={simulacao.simulacaoId}
        desconto={desconto}
        simulacaoAtualizada={simulacaoAtualizada}
        grupos={grupos}
        values={values}
        simulacao={simulacao}
        calcCif={calcCif}
        cliente={cliente}
        calcCifModal={calcCifModal}
        todosOsProdutos={todosOsProdutos}
        usuario={usuario}
        getTodayDate={getTodayDate}
      />

      <ModalEmail
        isOpen={show}
        onClose={() => setShow(false)}
        email={cliente?.email || usuario?.email}
        onConfirm={handleSend}
      />

      {loading && (
        <>
          <div className="spinner__form">
            <LoaderFull />
          </div>
        </>
      )}

      <ModalResponse
        isOpen={modalResponse?.isOpen}
        status={modalResponse.status}
        message={modalResponse?.message || "Ocorreu um erro ao processar a solicitação!"}
        closeModal={() => setModalResponse({ isOpen: false, message: "", status: "" })}
      />
    </>
  );
};

export default ListagemItem;
